/**
 * Phase 336 wave 3 task 3.3 (#1163) — the browser tab, in the client's words.
 *
 * <h3>Why the tab title is not an afterthought</h3>
 * <p>A client reading a report has our tab open beside their inbox, their ATS and the
 * spreadsheet these numbers replace. The tab strip is where they find us again, and it is the
 * one label the page itself never shows them. `<Page> — <their company>` answers both
 * questions a tab has to answer at eight characters wide: which report, and whose. It is also
 * the string that lands in a bookmark and in a browser-history search.</p>
 *
 * <h3>Why the tenant name, not ours</h3>
 * <p>The portal is single-tenant per sign-in (ADR-081: one client principal, exactly one
 * allowed tenant), so "Example Client" in the tab is a true statement about what is on screen
 * — and, on a shared machine, the fastest way to notice you are looking at the wrong client's
 * numbers. Until `/me` resolves there is no honest name to show, so the tab falls back to the
 * product title the HTML shell already carries rather than inventing one.</p>
 *
 * <h3>Why a hook, not a `<title>` per page</h3>
 * <p>Both report pages need the same rule, and a rule written twice is a rule that will be
 * changed once. The page passes only its own name; where the dash goes, what fills the second
 * half and what happens on the way out are decided here, once.</p>
 *
 * <p>It reads the identity itself rather than taking a name from its caller, for the same
 * reason: a caller that has to supply the second half is a caller that can supply the wrong
 * one. The extra `useClientTenant()` costs nothing — React Query dedupes `/me` by key, so the
 * page and this hook share one read.</p>
 */
import { useEffect, useRef } from "react";
import { useClientTenant } from "../api/portalQueries.js";

/**
 * The second half of the title before `/me` answers.
 *
 * <p>Mirrors `index.html`'s `<title>` deliberately: that is what the tab already says while
 * the bundle boots, so a page that cannot name the tenant yet leaves it alone rather than
 * flashing a half-built title at someone.</p>
 */
export const PORTAL_TITLE_FALLBACK = "AR Dept reports";

/** `<Page> — <tenant display name>`, or the product title while the tenant is unknown. */
export function pageTitle(page: string, tenantName: string | null): string {
  return `${page} — ${tenantName ?? PORTAL_TITLE_FALLBACK}`;
}

/**
 * Keep `document.title` naming this page, and this client, for as long as it is mounted.
 *
 * <p>The title is re-set when the tenant name arrives (it lands after the first paint — `/me`
 * is a network read), and the title that was there before this page mounted is put back when
 * it unmounts, so a route that sets no title of its own is never left wearing another page's.
 * React runs every cleanup in a commit before any effect, so navigating between the two
 * reports restores and then re-sets in that order, never the reverse.</p>
 */
export function usePageTitle(page: string): void {
  const { tenantName } = useClientTenant();

  // Captured on the first run only. Re-reading it each run would "restore" this hook's own
  // earlier title — the page name with the fallback half — instead of what the app had.
  const inherited = useRef<string | null>(null);

  useEffect(() => {
    if (inherited.current === null) inherited.current = document.title;
    document.title = pageTitle(page, tenantName);
  }, [page, tenantName]);

  useEffect(
    () => () => {
      if (inherited.current !== null) document.title = inherited.current;
    },
    [],
  );
}
